import { useMemo } from 'react';
import { useQuery, UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import { GET_LOCATIONS } from '../../providers/operations/getLocations';
import {
  GetLocationsQuery,
  GetLocationsQueryVariables,
  GeoServiceLocation,
  Maybe,
} from 'generated/graphql';

export type GeoServiceLocationsRequest = {
  geoNodeIds: Array<number>;
  iatas: Array<string>;
};

export type GetLocationsQueryResult = UseQueryResult<
  GetLocationsQuery,
  GetLocationsQueryVariables
>;

export type GeoServiceLocationsQuery = {
  data: GetLocationsQueryResult['data'];
  loading: GetLocationsQueryResult['loading'];
  errors: GetLocationsQueryResult['errors'];
};

export const useGeoServiceLocationsQuery = (
  geoServiceLocationsRequest: GeoServiceLocationsRequest,
  shouldSkip?: boolean
): GeoServiceLocationsQuery => {
  const { geoNodeIds = [], iatas = [] } = geoServiceLocationsRequest;
  const skipRequest = !geoNodeIds?.length && !iatas?.length;
  const { data, loading, errors } = useQuery<
    GetLocationsQuery,
    GetLocationsQueryVariables
  >(GET_LOCATIONS, {
    variables: {
      locationsRequest: {
        geoNodeIds,
        iatas,
      },
    },
    skip: skipRequest || !!shouldSkip,
  });

  return {
    data,
    loading: loading || skipRequest,
    errors,
  };
};

const getLocations = (
  locations: Array<Maybe<GeoServiceLocation>>,
  geoServiceLocationRequest: GeoServiceLocationsRequest
): Record<string, GeoServiceLocation> => {
  return locations.reduce((result, item) => {
    const itemIata = item?.iataCode ? item.iataCode : '';
    const itemGeoNodeId = item?.geoNodeId ? item.geoNodeId : -1;
    if (geoServiceLocationRequest?.geoNodeIds.includes(itemGeoNodeId)) {
      result[itemGeoNodeId.toString()] = { ...item };
    }
    if (geoServiceLocationRequest?.iatas.includes(itemIata)) {
      result[itemIata] = { ...item };
    }
    return result;
  }, {});
};

export const useGetLocations = (
  geoServiceLocationRequest: GeoServiceLocationsRequest,
  shouldSkip?: boolean
): Record<string, GeoServiceLocation> => {
  const { data, loading, errors } = useGeoServiceLocationsQuery(
    geoServiceLocationRequest,
    shouldSkip
  );

  return useMemo(() => {
    return !loading && !errors && !!data
      ? getLocations(data?.getLocations?.locations, geoServiceLocationRequest)
      : {};
  }, [data, loading, errors, geoServiceLocationRequest]);
};
